import { Form, Input, Modal, Select } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { GENDERS, USER_ROLES } from 'constants/users';
import { useOrganizationsStateContext } from 'providers/Organizations';
import {
  fetchOrganizationById,
  updateOrganization
} from 'services/api/organizations';
import { fetchRolesByParent, fetchRolesGroup } from 'services/api/roles';
import { RoleListRow } from 'types/api/roles';
import { createUser } from 'services/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  refetch(): void;
  onClose(): void;
  role?: string;
  id?: string;
}

const UsersModal: FC<IProps> = ({ id, role, refetch, onClose }) => {
  const [form] = Form.useForm();

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  const { results } = useOrganizationsStateContext();

  const [roleGroups, setRoleGroups] = useState<RoleListRow[]>([]);
  const [roles, setRoles] = useState<RoleListRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchRolesGroup();
        if (cancelled) return;
        setRoleGroups(rows);

        if (role) {
          const parentId = rows.find(item => item.name === role)?.id;

          if (parentId != null) {
            form.setFieldsValue({ parent_id: parentId });
          }
        }
      } catch {
        // swallow — empty options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [form, role]);

  const parentId = Form.useWatch('parent_id', form);

  const parent = roleGroups.find(item => String(item.id) === String(parentId));

  useEffect(() => {
    if (!parentId) {
      setRoles([]);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchRolesByParent(parentId);
        if (!cancelled) setRoles(rows);
      } catch {
        // swallow — empty options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [parentId]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const result = await fetchOrganizationById(id);
        if (cancelled || !result) return;
        form.setFieldsValue({
          name: result.name,
          additional_info: result.additional_info || ''
        });
      } catch {
        // swallow — empty form on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (parent?.name !== USER_ROLES.admin) {
      form.setFieldsValue({
        organizationIds: undefined
      });
    }
  }, [form, parent?.name]);

  const onFinish = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: Record<string, any>) => {
      if (id) {
        try {
          setIsUpdating(true);
          await updateOrganization(id, values);
          refetch();
          closeModal();
        } catch (err) {
          showErrorMessage(err);
        } finally {
          setIsUpdating(false);
        }

        return;
      }

      try {
        setIsCreating(true);
        await createUser(values);
        refetch();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsCreating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, refetch]
  );

  return (
    <Modal
      open
      title={id ? 'Edit' : 'Create'}
      onCancel={closeModal}
      width={600}
      onOk={form.submit}
      confirmLoading={isCreating || isUpdating}
      destroyOnClose
    >
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        <Form.Item
          name="first_name"
          label="First name"
          rules={[
            {
              required: true
            },
            {
              max: 25,
              min: 3
            },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          label="Last name"
          rules={[
            {
              required: true
            },
            {
              max: 25,
              min: 3
            },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
          <Select options={GENDERS} />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Phone number"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="parent_id"
          label="Role group"
          rules={[{ required: true }]}
          hidden={!!role}
        >
          <Select
            options={roleGroups.map(item => ({
              value: item.id,
              label: item.name
            }))}
          />
        </Form.Item>
        <Form.Item name="role_id" label="Role" rules={[{ required: true }]}>
          <Select
            options={roles.map(item => ({
              value: item.id,
              label: item.name
            }))}
          />
        </Form.Item>
        {parent?.name === USER_ROLES.admin && (
          <Form.Item
            name="organizations"
            label="Organizations"
            rules={[{ required: true }]}
          >
            <Select
              options={results.filter(item => item.value !== 'all')}
              mode="multiple"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default memo(UsersModal);
